import React from 'react';
import { useHistory } from 'react-router';
import { colorSet, theme } from '@components/Provider';
import { CustomizedTypography } from '@components/Customized';
import { ActiveTabType, ActiveTabTypeEnum } from '../../type';
import { Tabs, Tab, Chip, withStyles } from '@material-ui/core';
import useUserActionLog, {
  Enum_Action_Button_Enum,
  CapaActivityType
} from '@hooks/useUserActionLog';
import useDashboard from '@hooks/useDashboard';
import { GnbCount } from '@hooks/useGnbCount';
import { useActiveTab } from '@hooks/useActiveTab';
import { UpdateBadge } from '@components/UpdateBadge';

interface GnbProps {
  gnbCount: GnbCount;
}

const GnbTabsPresenter: React.FC<GnbProps> = ({ gnbCount }) => {
  const history = useHistory();
  const { onInitDashboard } = useDashboard();
  const { onInsertUserActionLogOne } = useUserActionLog();
  const activeTab = useActiveTab();

  const {
    newQuotationAndIntroductionCount,
    newChatCount,
    newPaymentManagementCount
  } = gnbCount;

  const hasNewPayment = !!newPaymentManagementCount;

  const StyledTabs = withStyles({
    root: {
      minHeight: 61,
      [theme.breakpoints.down('md')]: {
        minHeight: 68
      },
      color: colorSet.gray900
    },
    flexContainer: {
      '& button': {
        minHeight: 61,
        minWidth: 0,
        [theme.breakpoints.down('md')]: {
          minHeight: 68
        },
        '&.tab-one': {
          width: 126
        },
        '&.tab-two': {
          width: 100
        },
        '&.tab-three': {
          width: 154
        },
        opacity: 1
      },
      '& .tab-wrapper': {
        display: 'flex',
        alignItems: 'center',
        '& .MuiChip-root': {
          width: 32,
          height: 20,
          marginLeft: 4,
          color: colorSet.gray000,
          backgroundColor: '#FF7F7C',
          pointerEvents: 'none',
          '& .MuiChip-label': {
            padding: 0
          }
        }
      }
    },
    indicator: {
      display:
        activeTab === ActiveTabTypeEnum.DASHBOARD ||
        activeTab === ActiveTabTypeEnum.CHAT ||
        activeTab === ActiveTabTypeEnum.PAYMENT
          ? 'flex'
          : 'none',
      justifyContent: 'center',
      backgroundColor: 'transparent',
      '& > span': {
        height: 2,
        width: '100%',
        backgroundColor: colorSet.gray800
      }
    }
  })((props: any) => (
    <Tabs {...props} TabIndicatorProps={{ children: <span /> }} />
  ));

  return (
    <StyledTabs
      value={activeTab}
      onChange={(event: React.ChangeEvent, value: ActiveTabType) => {
        onInitDashboard();
        if (value === ActiveTabTypeEnum.DASHBOARD) {
          onInsertUserActionLogOne(
            Enum_Action_Button_Enum.Button_Gnb_Tab_Dashboard,
            CapaActivityType.ButtonClick
          );
          history.push('/dashboard');
        } else if (value === ActiveTabTypeEnum.CHAT) {
          onInsertUserActionLogOne(
            Enum_Action_Button_Enum.Button_Gnb_Tab_Chat,
            CapaActivityType.ButtonClick
          );
          history.push('/chat');
        } else if (value === ActiveTabTypeEnum.PAYMENT) {
          history.push('/payment');
        }
      }}
    >
      <Tab
        className="tab-one"
        value={ActiveTabTypeEnum.DASHBOARD}
        label={
          <div className="tab-wrapper">
            <CustomizedTypography variant="body2" fontWeight="500">
              견적비교
            </CustomizedTypography>
            {newQuotationAndIntroductionCount > 0 ? (
              <Chip
                label={
                  newQuotationAndIntroductionCount > 99
                    ? '99+'
                    : newQuotationAndIntroductionCount
                }
              />
            ) : null}
          </div>
        }
      />
      <Tab
        className="tab-two"
        value={ActiveTabTypeEnum.CHAT}
        label={
          <div className="tab-wrapper">
            <CustomizedTypography variant="body2" fontWeight="500">
              채팅
            </CustomizedTypography>
            {newChatCount > 0 ? (
              <Chip label={newChatCount > 99 ? '99+' : newChatCount} />
            ) : null}
          </div>
        }
      />

      <Tab
        className="tab-three"
        value={ActiveTabTypeEnum.PAYMENT}
        label={
          <div className="tab-wrapper">
            <UpdateBadge invisible={!hasNewPayment}>
              <CustomizedTypography variant="body2" fontWeight="500">
                발주관리
              </CustomizedTypography>
            </UpdateBadge>
          </div>
        }
      />
    </StyledTabs>
  );
};

export default GnbTabsPresenter;
